import React, { useCallback, useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { orientationApi } from '../../api/orientation';
import './OrientationDashboard.css';

// All theming is via CSS custom properties (var(--color-*)), which adapt
// across dark / light / paperwhite — so this component needs no JS color
// values and does not pull useTheme().colors.

/**
 * Orientation Dashboard — read-only, derived "state of the story".
 *
 * Replaces the StoryGraph panel at /graph. Story-scoped (currentStory
 * from AppContext). Six glanceable tiles, all derived on demand: phase
 * arc, coverage checklist, spine, next-commitment nudge, open threads,
 * last worked. No persistence, no LLM. Jet-age departure-board feel.
 */

const PHASE_LABELS = {
  foundation: 'Foundation',
  coalescence: 'Coalescence',
  convergence: 'Convergence',
  consolidation: 'Consolidation',
};

const PHASE_BLURB = {
  foundation: 'finding the story',
  coalescence: 'anchors taking hold',
  convergence: 'structure coming together',
  consolidation: 'filling it in',
};

// Coverage checklist rows — the six commitments + the structural reads.
const COMMITMENT_ROWS = [
  ['logline_committed', 'Logline'],
  ['theme_committed', 'Theme'],
  ['protagonist_confirmed', 'Protagonist'],
  ['world_grounded', 'World'],
  ['acts_structured', 'Acts'],
  ['outline_workshop_started', 'Outline workshop'],
];

function spineEmptyState(phase) {
  const where = phase ? PHASE_LABELS[phase].toLowerCase() : 'foundation';
  return `Your story's spine will settle here as the story comes into focus — you're in ${where}, which is exactly where it gets found.`;
}

function relativeWhen(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

export default function OrientationDashboard() {
  const { currentStory } = useApp();
  const storyId = currentStory?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!storyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await orientationApi.get(storyId);
      setData(response?.data || response);
    } catch (err) {
      setError(err.message || 'Failed to load the dashboard');
    } finally {
      setLoading(false);
    }
  }, [storyId]);

  useEffect(() => { load(); }, [load]);

  if (!storyId) {
    return (
      <div className="od-page">
        <div className="od-empty">
          The dashboard is story-scoped. Open a story to see where it stands.
        </div>
      </div>
    );
  }
  if (loading) {
    return <div className="od-page"><div className="od-empty">Reading the story…</div></div>;
  }
  if (error) {
    return <div className="od-page"><div className="od-empty">{error}</div></div>;
  }
  if (!data) return null;

  const {
    phase, phase_order: phaseOrder = [], signals, next_commitment: nextCommitment,
    all_committed: allCommitted, log_line: logLine, theme, open_threads: openThreads = [],
    last_worked: lastWorked,
  } = data;

  const currentIndex = phase ? phaseOrder.indexOf(phase) : -1;

  return (
    <div className="od-page">
      {/* ── Phase arc: a dashed flight-route line with waypoints ── */}
      <section className="od-arc" aria-label="Lifecycle phase">
        <div className="od-section-label">You are here</div>
        <div className="od-arc__route">
          <div className="od-arc__line" aria-hidden="true" />
          {phaseOrder.map((p, i) => {
            const state =
              currentIndex === -1 ? 'future'
                : i < currentIndex ? 'past'
                  : i === currentIndex ? 'current' : 'future';
            return (
              <div key={p} className={`od-waypoint od-waypoint--${state}`}>
                {state === 'current' && <div className="od-waypoint__marker">you are here</div>}
                <div className="od-waypoint__dot" />
                <div className="od-waypoint__name">{PHASE_LABELS[p]}</div>
              </div>
            );
          })}
        </div>
        {phase ? (
          <div className="od-arc__blurb">{PHASE_BLURB[phase]}</div>
        ) : (
          <div className="od-arc__blurb">
            This book hasn't started yet — its phase appears once there's a story to read.
          </div>
        )}
      </section>

      {/* ── Spine: the logline, large and warm; placeholder when absent ── */}
      <section className="od-spine" aria-label="Story spine">
        <div className="od-section-label">Spine</div>
        {logLine ? (
          <p className="od-spine__line">{logLine}</p>
        ) : (
          <p className="od-spine__placeholder">{spineEmptyState(phase)}</p>
        )}
        {theme && <p className="od-spine__theme">Theme — {theme}</p>}
      </section>

      {/* ── Next commitment nudge ── */}
      {signals && (
        <section className="od-nudge" aria-label="Next commitment">
          {nextCommitment ? (
            <p className="od-nudge__line">
              <span className="od-nudge__caret">→</span> Next: {nextCommitment.label}
            </p>
          ) : allCommitted ? (
            <p className="od-nudge__line od-nudge__line--good">
              Every commitment's in — you're in a good place.
            </p>
          ) : null}
        </section>
      )}

      <div className="od-lower">
        {/* ── Coverage checklist ── */}
        {signals && (
          <section className="od-coverage" aria-label="Coverage">
            <div className="od-section-label">Coverage</div>
            <ul className="od-checklist">
              {COMMITMENT_ROWS.map(([key, label]) => {
                const met = signals[key];
                return (
                  <li key={key} className={`od-check ${met ? 'od-check--met' : 'od-check--unmet'}`}>
                    <span className="od-check__mark" aria-hidden="true">{met ? '●' : '○'}</span>
                    {label}
                  </li>
                );
              })}
            </ul>
            <div className="od-coverage__reads">
              {signals.open_count > 0 && (
                <span className="od-pill">
                  {signals.open_count} open question{signals.open_count === 1 ? '' : 's'}
                  {signals.structure_critical_open_count > 0
                    && ` · ${signals.structure_critical_open_count} structural`}
                </span>
              )}
              {signals.outline_coverage.total_sections > 0 && (
                <span className="od-pill">
                  outline {signals.outline_coverage.workshopped_sections}/{signals.outline_coverage.total_sections} workshopped
                </span>
              )}
            </div>
          </section>
        )}

        {/* ── Open threads ── */}
        <section className="od-threads" aria-label="Open threads">
          <div className="od-section-label">Open threads</div>
          {openThreads.length > 0 ? (
            <ul className="od-threads__list">
              {openThreads.map((t, i) => (
                <li key={i} className="od-threads__item">{t.question_text}</li>
              ))}
            </ul>
          ) : (
            <p className="od-threads__empty">No open threads — nothing dangling right now.</p>
          )}
        </section>
      </div>

      {/* ── Last worked ── */}
      {lastWorked && (
        <div className="od-lastworked">
          Last open {relativeWhen(lastWorked.last_activity)}
          {lastWorked.name ? ` · ${lastWorked.name}` : ''}
        </div>
      )}
    </div>
  );
}
